import { faBars, faTimes } from '@fortawesome/pro-light-svg-icons';
import { faGridHorizontal, faUserCircle, IconDefinition } from '@fortawesome/pro-thin-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Surface, SurfaceClipShadow } from '@partful/shared/ui/Surface';
import { useTenant } from '@providers/Tenant';
import { useUser } from '@providers/User';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import samsonLogoUrl from 'src/assets/logo-default.svg';
import styles from './PageNavigation.module.scss';

type NavRoute = { label: ReactNode; path: string; icon: IconDefinition; enabled?: boolean };
type NavigationProps = { navRoutes: NavRoute[]; activeRoute?: NavRoute };

export function PageNavigation() {
  const { enabledFeatures } = useTenant();
  const { t: i18n } = useTranslation();
  const { pathname } = useLocation();

  const navRoutes: NavRoute[] = [
    {
      label: i18n('navigation:library'),
      path: '/instructions',
      icon: faGridHorizontal,
      enabled: enabledFeatures.includes('instructions'),
    },
    {
      label: i18n('navigation:myAccount'),
      path: '/my-account',
      icon: faUserCircle,
    },
  ];

  const activeRoute = navRoutes.find(({ path }) => pathname.startsWith(path));

  return (
    <>
      <MobilePageNavigation navRoutes={navRoutes} activeRoute={activeRoute} />
      <DesktopPageNavigation navRoutes={navRoutes} activeRoute={activeRoute} />
    </>
  );
}

type MobilePageNavigationContentProps = {
  render: ({ close }: { close: () => void }) => ReactNode;
};
export function MobilePageNavigationContent({ render }: MobilePageNavigationContentProps) {
  const [mobileNav, setMobileNav] = useState<HTMLElement | null>();

  useEffect(() => {
    setMobileNav(document.getElementById('mobile-page-navigation-content'));
  }, []);

  if (mobileNav) {
    return createPortal(render({ close: () => mobileNav.dispatchEvent(new CustomEvent('close-menu')) }), mobileNav);
  }

  return null;
}

function MobilePageNavigation({ navRoutes, activeRoute }: Readonly<NavigationProps>) {
  const { logoUrl } = useUser();
  const { name: tenantName } = useTenant();
  const [open, setOpen] = useState(false);
  const { t: i18n } = useTranslation();

  const mobileNavContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleCloseFromContent() {
      setOpen(false);
    }

    const mobileNavContent = mobileNavContentRef.current;
    mobileNavContent?.addEventListener('close-menu', handleCloseFromContent);

    return () => {
      mobileNavContent?.removeEventListener('close-menu', handleCloseFromContent);
    };
  }, [mobileNavContentRef]);

  return (
    <Surface shadow="large" glass className={styles.mobilePageNavigation} data-testid="mobile-page-navigation">
      <button
        type="button"
        className={styles.menuButton}
        onClick={() => setOpen(true)}
        aria-label={i18n('navigation:openMenu')}
      >
        <FontAwesomeIcon icon={faBars} />
      </button>
      <div className={styles.branding}>
        <img src={logoUrl ?? samsonLogoUrl} alt={tenantName} />
      </div>
      <div className={styles.panelContainer} data-open={open}>
        <div
          aria-hidden
          className={styles.backdrop}
          onClick={() => setOpen(false)}
          data-testid="mobile-page-navigation-backdrop"
        />
        <Surface
          shadow="large"
          glass
          className={styles.panel}
          data-open={open}
          data-testid="mobile-page-navigation-panel"
        >
          <div className={styles.panelBranding}>
            <img src={logoUrl ?? samsonLogoUrl} alt={tenantName} />
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => setOpen(false)}
              aria-label={i18n('navigation:closeMenu')}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <div
            className={styles.navContent}
            id="mobile-page-navigation-content"
            data-testid="mobile-page-navigation-content"
            ref={mobileNavContentRef}
          />
          <nav className={styles.nav}>
            {navRoutes.map(
              ({ enabled, label, path, icon }) =>
                enabled !== false && (
                  <NavLink
                    key={path}
                    to={path}
                    aria-current={path === activeRoute?.path ? 'page' : undefined}
                    onClick={() => setOpen(false)}
                  >
                    <FontAwesomeIcon icon={icon} />
                    <span>{label}</span>
                  </NavLink>
                )
            )}
          </nav>
        </Surface>
      </div>
    </Surface>
  );
}

function DesktopPageNavigation({ navRoutes, activeRoute }: Readonly<NavigationProps>) {
  return (
    <SurfaceClipShadow shadow="large" className={styles.desktopPageNavigation} data-testid="desktop-page-navigation">
      <Surface clip="bottom-left" clipSize="large" glass className={styles.pageNavigation}>
        <nav>
          {navRoutes.map(
            ({ enabled, label, path, icon }) =>
              enabled !== false && (
                <NavLink key={path} to={path} aria-current={path === activeRoute?.path ? 'page' : undefined}>
                  <FontAwesomeIcon icon={icon} />
                  <span>{label}</span>
                </NavLink>
              )
          )}
        </nav>
      </Surface>
    </SurfaceClipShadow>
  );
}
